import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { hybridAuthService } from '../services/hybridAuthService';
import { userProfileService } from '../services/userProfileService';
import {
  initLocalDevTenantContext,
  hasDevTenantContext,
  isLocalDevHost,
} from '../services/devTenantContext';
import { licenseService } from '../services/licenseService';
import type { PublicTenantDto } from '../types/publicTenant';
import { authConfig, usesEnvApiCredentials } from '../config/auth';
import { getUserRoleCode } from '../utils/userRoleUtils';
import {
  buildSpaTokenRequestBody,
  clearTenantBootstrapContext,
  getCachedPublicTenant,
  resolveTenantContext,
  resolveTenantContextDetailed,
  shouldFetchPublicTenantOnBootstrap,
  type TenantBootstrapState,
} from '../services/tenantContextService';

interface Role {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: string;
  color?: string;
  labelColor?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  isAdmin: boolean;
  isOwnerCustomer?: boolean;
  avatarBase64?: string;
  customer_id?: string;
  role?: Role;
  authenticationType?: string;
  teams?: string[];
}

const parseIsOwnerClaim = (value: unknown): boolean => {
  if (typeof value === 'boolean')
  {
    return value;
  }

  if (typeof value === 'string')
  {
    return value.toLowerCase() === 'true';
  }

  return false;
};

const extractRoleCodeFromClaim = (role: unknown): string => {
  if (Array.isArray(role)) {
    const firstRole = role[0];
    if (typeof firstRole === 'string') {
      return firstRole.toLowerCase();
    }

    return '';
  }

  if (typeof role === 'string') {
    return role.toLowerCase();
  }

  return '';
};

const withNormalizedAdminFlag = (userData: User): User => ({
  ...userData,
  isAdmin: getUserRoleCode(userData) === 'admin',
});

interface AuthTokens {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
}

interface AuthContextData {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  publicTenant: PublicTenantDto | null;
  tenantBootstrap: TenantBootstrapState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  updateUser: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

// Função para armazenar tokens de forma segura
const secureStorage = {
  setTokens: (tokens: AuthTokens) => {
    try {
      // Usar sessionStorage para tokens (mais seguro que localStorage)
      sessionStorage.setItem('@KnowInDoc:accessToken', tokens.accessToken);
      sessionStorage.setItem('@KnowInDoc:refreshToken', tokens.refreshToken || '');
      sessionStorage.setItem('@KnowInDoc:expiresAt', (Date.now() + tokens.expiresIn * 1000).toString());
      
      // Verificar se foram salvos corretamente
      const savedAccessToken = sessionStorage.getItem('@KnowInDoc:accessToken');
      const savedRefreshToken = sessionStorage.getItem('@KnowInDoc:refreshToken');
      const savedExpiresAt = sessionStorage.getItem('@KnowInDoc:expiresAt');
      
    } catch (error) {
      console.error('Erro ao salvar tokens no secureStorage:', error);
    }
  },

  getTokens: (): AuthTokens | null => {
    try {
      const accessToken = sessionStorage.getItem('@KnowInDoc:accessToken');
      const refreshToken = sessionStorage.getItem('@KnowInDoc:refreshToken');
      const expiresAt = sessionStorage.getItem('@KnowInDoc:expiresAt');

      // Verificar se pelo menos o accessToken existe
      if (!accessToken) {
        return null;
      }

      // Se não há expiresAt, calcular baseado no token
      let expiresIn = 0;
      if (expiresAt) {
        expiresIn = parseInt(expiresAt) - Date.now();
      } else {
        // Tentar extrair expiração do token
        const decoded = decodeJwt(accessToken);
        if (decoded && decoded.exp) {
          expiresIn = (decoded.exp * 1000) - Date.now();
        } else {
          expiresIn = 3600000; // 1 hora padrão
        }
      }



      return {
        accessToken,
        refreshToken: refreshToken === '' ? null : refreshToken,
        expiresIn: expiresIn
      };
    } catch (error) {
      console.error('Erro ao recuperar tokens do secureStorage:', error);
      return null;
    }
  },

  clearTokens: () => {
    try {
      sessionStorage.removeItem('@KnowInDoc:accessToken');
      sessionStorage.removeItem('@KnowInDoc:refreshToken');
      sessionStorage.removeItem('@KnowInDoc:expiresAt');
      sessionStorage.removeItem('@KnowInDoc:user');
      sessionStorage.removeItem('@KnowInDoc:customer_id');
      sessionStorage.removeItem('@KnowInDoc:user_id');
      sessionStorage.removeItem('@KnowInDoc:apiToken');
      sessionStorage.removeItem('@KnowInDoc:customerToken');
      sessionStorage.removeItem('@KnowInDoc:userToken');

    } catch (error) {
      console.error('Erro ao limpar tokens do secureStorage:', error);
    }
  }
};

// Função para decodificar JWT e verificar expiração
const decodeJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Função para verificar se token está expirado
const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [publicTenant, setPublicTenant] = useState<PublicTenantDto | null>(null);
  const [tenantBootstrap, setTenantBootstrap] = useState<TenantBootstrapState>({
    kind: 'loading',
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initLocalDevTenantContext(searchParams);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadPublicTenant = async () => {
      if (!shouldFetchPublicTenantOnBootstrap()) {
        if (!cancelled) {
          setPublicTenant(null);
          setTenantBootstrap({ kind: 'skipped' });
        }
        return;
      }
      if (!cancelled) setTenantBootstrap({ kind: 'loading' });
      const result = await resolveTenantContextDetailed(true);
      if (cancelled) return;
      if (result.status === 'ok') {
        setPublicTenant(result.tenant);
        setTenantBootstrap({ kind: 'success' });
      } else if (result.status === 'not_found') {
        setPublicTenant(null);
        setTenantBootstrap({ kind: 'not_found' });
      } else {
        setPublicTenant(null);
        setTenantBootstrap({ kind: 'unavailable' });
      }
    };

    loadPublicTenant();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Função para carregar dados do usuário do token
  const loadUserFromToken = (token: string): User | null => {
    const decoded = decodeJwt(token);
    if (!decoded) {
      console.error('❌ Token não pôde ser decodificado');
      return null;
    }

    const userId = decoded.sub || 
                   decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
                   decoded.UserId ||
                   decoded.userId;
    
    const customerId =
      decoded.CustomerId ||
      decoded.customerId ||
      decoded.customer_id ||
      decoded['http://schemas.microsoft.com/identity/claims/tenantid'];
    
    const userName = decoded.name || 
                     decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
                     decoded.UserName ||
                     decoded.userName;
    
    const userEmail = decoded.email || 
                      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
                      decoded.Email ||
                      decoded.email;
    
    const userRole = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
                     decoded.Role ||
                     decoded.role;
    const roleCode = extractRoleCodeFromClaim(userRole);

    const userData = {
      id: userId,
      name: userName,
      email: userEmail,
      isAdmin: roleCode === 'admin',
      isOwnerCustomer: parseIsOwnerClaim(decoded.IsOwner ?? decoded.isOwner),
      customer_id: customerId,
      role: userRole,
      authenticationType: decoded.authenticationType || 'local'
    };

    return withNormalizedAdminFlag(userData as User);
  };

  // Função para carregar dados completos do usuário incluindo avatar
  const loadCompleteUserProfile = async (userId: string): Promise<User | null> => {
    try {
      const userProfile = await userProfileService.getUserProfile(userId);
      
      if (userProfile) {
        return userProfile;
      }
      
      return null;
    } catch (error) {
      console.error('AuthContext: Erro ao carregar perfil completo:', error);
      return null;
    }
  };

  // Função para renovar token automaticamente
  const refreshAuth = async (): Promise<boolean> => {
    try {
      const tokens = secureStorage.getTokens();
      if (!tokens || !tokens.refreshToken) {
        return false;
      }

      // Garantir que refreshToken não é null
      const refreshToken = tokens.refreshToken;
      if (isTokenExpired(refreshToken)) {
        return false;
      }

      const response = await api.post('/auth/refresh', {
        refreshToken: refreshToken
      });

      if (response.data.token) {
        const newTokens: AuthTokens = {
          accessToken: response.data.token,
          refreshToken: response.data.refreshToken || null,
          expiresIn: response.data.expiresIn || 3600
        };

        secureStorage.setTokens(newTokens);
        
        const userData = loadUserFromToken(newTokens.accessToken);
        if (userData) {
          setUser(userData);
          sessionStorage.setItem('@KnowInDoc:user', JSON.stringify(userData));
          sessionStorage.setItem('@KnowInDoc:customer_id', userData.customer_id || '');
          return true;
        }
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  // Função para verificar e renovar token automaticamente
  const checkAndRefreshToken = async (): Promise<boolean> => {
    const tokens = secureStorage.getTokens();
    if (!tokens) return false;

    // Se o token está próximo de expirar (5 minutos), renova
    const timeUntilExpiry = tokens.expiresIn;
    if (timeUntilExpiry < 300000) { // 5 minutos
      return await refreshAuth();
    }

    return true;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const tokens = secureStorage.getTokens();
        
        if (tokens && !isTokenExpired(tokens.accessToken)) {
          const userData = loadUserFromToken(tokens.accessToken);
          if (userData) {
            // Carregar perfil completo incluindo avatar
            const completeProfile = await loadCompleteUserProfile(userData.id);
            const finalUserData = completeProfile
              ? withNormalizedAdminFlag({
                  ...userData,
                  ...completeProfile,
                  customer_id: completeProfile.customer_id ?? userData.customer_id,
                  authenticationType: userData.authenticationType,
                })
              : userData;

            setUser(finalUserData);
            sessionStorage.setItem('@KnowInDoc:user', JSON.stringify(finalUserData));
            sessionStorage.setItem('@KnowInDoc:customer_id', finalUserData.customer_id || '');
          } else {
            // Tentar renovar token se dados do usuário não estão válidos
            const refreshed = await refreshAuth();
            if (!refreshed) {
              secureStorage.clearTokens();
            }
          }
        } else if (tokens && tokens.refreshToken && !isTokenExpired(tokens.refreshToken)) {
          // Tentar renovar com refresh token
          const refreshed = await refreshAuth();
          if (!refreshed) {
            secureStorage.clearTokens();
          }
        } else {
          // Limpar dados inválidos
          secureStorage.clearTokens();
        }
      } catch (error) {
        console.error('Erro na inicialização da autenticação:', error);
        secureStorage.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Listener para sincronização entre abas
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Verificar se é um evento de logout de outra aba
      // O evento storage só é disparado em OUTRAS abas quando localStorage muda
      if (event.key === '@KnowInDoc:logout_event' && event.newValue) {
        console.log('Logout detectado em outra aba, fazendo logout local...');
        setUser(null);
        secureStorage.clearTokens();
        // Redirecionar para login se necessário
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    };

    // Adicionar listener para mudanças no localStorage
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Monitorar mudanças no user e isAuthenticated
  useEffect(() => {
  }, [user]);

  const signIn = async (email: string, password: string) => {
    try {
      const envOnlyLocal =
        usesEnvApiCredentials() &&
        isLocalDevHost() &&
        !hasDevTenantContext();

      let customerResponse;

      if (envOnlyLocal) {
        customerResponse = await hybridAuthService.authenticateCustomer({
          ClientId: authConfig.apiClientId,
          ClientSecret: authConfig.apiClientSecret,
        });
      } else {
        const ctx =
          (await resolveTenantContext(true)) ?? getCachedPublicTenant();
        if (!ctx?.clientId) {
          throw new Error(
            'Tenant não encontrado. Em localhost use ?tenantSlug=..., variáveis REACT_APP_DEV_* ou credenciais REACT_APP_API_* no .env.'
          );
        }

        const spaBody = buildSpaTokenRequestBody();
        if (!spaBody) {
          throw new Error(
            'Não foi possível validar o tenant (host/slug). Verifique a URL ou o ambiente de desenvolvimento.'
          );
        }

        customerResponse = await hybridAuthService.authenticateSpaTenant(spaBody);
      }

      const userResponse = await hybridAuthService.authenticateUser({
        Email: email,
        Password: password,
        CustomerId: customerResponse.customerId
      });

      const hybridResponse = await hybridAuthService.createHybridToken({
        CustomerToken: customerResponse.token,
        UserToken: userResponse.token
      });

      const tokens: AuthTokens = {
        accessToken: hybridResponse.token,
        refreshToken: hybridResponse.refreshToken || userResponse.refreshToken || null,
        expiresIn: hybridResponse.expiresIn || userResponse.expiresIn || 3600
      };

      secureStorage.setTokens(tokens);

      const userData = loadUserFromToken(hybridResponse.token);
      if (userData) {
        const completeProfile = await loadCompleteUserProfile(userData.id);
        const finalUserData = completeProfile
          ? withNormalizedAdminFlag({
              ...userData,
              ...completeProfile,
              customer_id: completeProfile.customer_id ?? userData.customer_id,
              authenticationType: userData.authenticationType,
            })
          : userData;

        setUser(finalUserData);
        sessionStorage.setItem('@KnowInDoc:user', JSON.stringify(finalUserData));
        sessionStorage.setItem('@KnowInDoc:customer_id', finalUserData.customer_id || '');
        sessionStorage.setItem('@KnowInDoc:user_id', finalUserData.id);

        sessionStorage.setItem('@KnowInDoc:accessToken', hybridResponse.token);
        sessionStorage.setItem('@KnowInDoc:refreshToken', hybridResponse.refreshToken || userResponse.refreshToken || '');
        sessionStorage.setItem('@KnowInDoc:customerToken', customerResponse.token);
        sessionStorage.setItem('@KnowInDoc:userToken', userResponse.token);
      } else {
        console.error('❌ Falha ao carregar dados do usuário do token');
        throw new Error('Dados do usuário inválidos');
      }
    } catch (error: any) {
      console.error('Erro no login:', error);
      throw new Error(error.message || 'Erro ao fazer login');
    }
  };

  const signOut = async () => {
    try {
      // Chamar endpoint de logout no backend
      const tokens = secureStorage.getTokens();
      if (tokens?.accessToken) {
        try {
          await api.post('/auth/logout', {}, {
            headers: {
              'Authorization': `Bearer ${tokens.accessToken}`
            }
          });
        } catch (error) {
          // Log do erro mas não impedir o logout local
          console.warn('Erro ao fazer logout no servidor:', error);
        }
      }

      // Limpar dados localmente
      setUser(null);
      secureStorage.clearTokens();
      licenseService.clearCachedLicense();
      clearTenantBootstrapContext();

      // Notificar outras abas sobre o logout usando localStorage
      // O evento storage só funciona com localStorage, não sessionStorage
      localStorage.setItem('@KnowInDoc:logout_event', Date.now().toString());
      localStorage.removeItem('@KnowInDoc:logout_event'); // Remove imediatamente para limpar

    } catch (error) {
      console.error('Erro no logout:', error);
      // Mesmo com erro, limpar dados localmente
      setUser(null);
      secureStorage.clearTokens();
      licenseService.clearCachedLicense();
      clearTenantBootstrapContext();
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    sessionStorage.setItem('@KnowInDoc:user', JSON.stringify(updatedUser));
    if (updatedUser.customer_id !== undefined) {
      sessionStorage.setItem('@KnowInDoc:customer_id', updatedUser.customer_id || '');
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Carregando...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated: !!user, 
      isLoading,
      publicTenant,
      tenantBootstrap,
      signIn, 
      signOut,
      refreshAuth,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 